import { useEffect, useState } from 'react';
import { request } from '../api';
import { Card, Id, Moment, Table } from './kit';
import { auditActionLabel } from './audit-actions';

/**
 * Общая часть журнала действий: разбор «было → стало» и короткая история по
 * записи.
 *
 * Живёт в оболочке, а не в разделе «Журнал», потому что нужна в трёх местах:
 * в самом разделе, в карточке клиента (M1-13 «по любой записи можно получить
 * последовательность изменений») и в карточке сотрудника. Разделы админки друг
 * у друга не занимают — общее уходит сюда.
 */

export interface AuditActor {
  readonly type?: string;
  readonly id?: string;
  readonly displayName?: string;
}

export interface AuditEntry {
  readonly id: string;
  readonly actor: AuditActor | null;
  readonly action: string;
  readonly entityType: string;
  readonly entityId: string | null;
  readonly siteId: string | null;
  readonly before: unknown;
  readonly after: unknown;
  readonly createdAt: string;
  readonly requestId: string | null;
  readonly ip: string | null;
  readonly userAgent: string | null;
}

const ACTOR_KIND: Readonly<Record<string, string>> = {
  system: 'Система',
  device: 'Устройство',
  service: 'Служба',
};

/** Кто. Имя сотрудника при записи не маскируется — иначе журнал не отвечает «кто». */
export function actorName(actor: AuditActor | null | undefined): string {
  if (!actor) return 'Неизвестно';
  if (actor.displayName) return actor.displayName;
  if (actor.type && ACTOR_KIND[actor.type]) return ACTOR_KIND[actor.type] as string;
  return actor.id ?? 'Неизвестно';
}

export interface FieldDiff {
  readonly field: string;
  readonly before: unknown;
  readonly after: unknown;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Что именно изменилось.
 *
 * Сравниваются только верхние поля: вложенное показывается целиком, потому что
 * «изменилось `payload.items[2].qty`» человеку не говорит ничего, а весь объект
 * рядом — говорит. Значения сравниваются по JSON: в журнале они и лежат JSON-ом,
 * и порядок ключей в нём стабилен, так как пишется одним `JSON.stringify`.
 */
export function diffFields(before: unknown, after: unknown): readonly FieldDiff[] {
  if (!isObject(before) && !isObject(after)) return [];

  const left = isObject(before) ? before : {};
  const right = isObject(after) ? after : {};
  const keys = [...new Set([...Object.keys(left), ...Object.keys(right)])].sort();

  const diffs: FieldDiff[] = [];
  for (const field of keys) {
    const a = left[field];
    const b = right[field];
    if (JSON.stringify(a) === JSON.stringify(b)) continue;
    diffs.push({ field, before: a, after: b });
  }
  return diffs;
}

/** Значение поля в читаемом виде: строки без кавычек, пустое — прочерком. */
export function fieldValue(value: unknown): string {
  if (value === undefined) return '—';
  if (value === null) return '∅';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

const FIELD_LABELS: Readonly<Record<string, string>> = {
  status: 'статус',
  display_name: 'имя',
  phone: 'телефон',
  amount_minor: 'сумма',
  site_id: 'объект',
  role: 'роль',
  login: 'логин',
  reason: 'основание',
  comment: 'комментарий',
  published_at: 'опубликовано',
  archived_at: 'в архиве с',
  exited_at: 'выезд',
  entered_at: 'въезд',
};

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

/** Таблица «поле — было — стало». */
export function AuditDiff({ before, after }: { before: unknown; after: unknown }) {
  const diffs = diffFields(before, after);

  if (diffs.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        Сравнивать нечего: запись не хранит состояния до и после.
      </p>
    );
  }

  return (
    <Table>
      <thead>
        <tr>
          <th className="w-1/3">Поле</th>
          <th>Было</th>
          <th>Стало</th>
        </tr>
      </thead>
      <tbody>
        {diffs.map((d) => (
          <tr key={d.field}>
            <td className="align-top text-ink-muted">{fieldLabel(d.field)}</td>
            <td className="align-top break-all">{fieldValue(d.before)}</td>
            <td className="align-top break-all font-medium">{fieldValue(d.after)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Короткая сводка изменений для строки таблицы. */
export function changeSummary(entry: AuditEntry): string {
  if (entry.before === null && entry.after !== null) return 'создано';
  if (entry.after === null && entry.before !== null) return 'удалено';
  const count = diffFields(entry.before, entry.after).length;
  if (count === 0) return 'без сравнения';
  if (count === 1) return fieldLabel(diffFields(entry.before, entry.after)[0]?.field ?? '');
  return `полей: ${count}`;
}

interface AuditPage {
  readonly items: readonly AuditEntry[];
  readonly nextCursor: string | null;
}

/**
 * История по одной записи — секция карточки.
 *
 * Отказ по правам не показывается вовсе: кассир не должен видеть на карточке
 * клиента красную плашку «нет доступа к журналу», это граница роли, а не сбой.
 */
export function AuditTrail({
  entityId,
  title = 'История изменений',
  limit = 6,
}: {
  entityId: string;
  title?: string;
  limit?: number;
}) {
  const [rows, setRows] = useState<readonly AuditEntry[] | null>(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setDenied(false);
    request<AuditPage>(
      'GET',
      `/api/v1/audit?entity_id=${encodeURIComponent(entityId)}&limit=${limit}`,
    )
      .then((page) => {
        if (alive) setRows(page.items);
      })
      .catch((err: { status?: number }) => {
        if (!alive) return;
        if (err?.status === 403) setDenied(true);
        setRows([]);
      });
    return () => {
      alive = false;
    };
  }, [entityId, limit]);

  if (denied) return null;

  return (
    <Card title={title}>
      {rows === null ? (
        <p className="text-sm text-ink-muted">Загружаем историю…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-ink-muted">Изменений не было.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id} className="border-b border-line pb-2 text-sm last:border-0 last:pb-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                {/* Действие словами: код на латинице требует переводчика ровно
                    в тот момент, когда журнал и читают — при разборе спора. */}
                <span className="font-medium text-ink">{auditActionLabel(row.action)}</span>
                <span className="text-xs text-ink-muted">
                  <Moment iso={row.createdAt} /> · {actorName(row.actor)}
                </span>
              </div>
              <div className="text-xs text-ink-muted">{changeSummary(row)}</div>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3">
        <a
          className="text-sm text-brand-700 hover:underline"
          href={`#/audit/actions/${encodeURIComponent(entityId)}`}
        >
          Весь журнал по этой записи
        </a>
      </div>
    </Card>
  );
}

/** Идентификатор записи журнала в компактном виде — для строк таблицы. */
export function EntityRef({ type, id }: { type: string; id: string | null }) {
  return (
    <span className="text-xs">
      <span className="text-ink-muted">{type}</span>
      {id ? (
        <>
          {' · '}
          <Id value={id} />
        </>
      ) : null}
    </span>
  );
}
